import Typography from '@mui/material/Typography';
import LinkButton from 'atoms/LinkButton';
import Link from 'atoms/NextLink';
import cn from 'classnames';
import Image from 'next/image';
import React from 'react';
import { getPromoFields } from 'stores/offeringUtils';
import { IOfferingInfo } from 'utils/globalTypes';
import { defaultLoader } from 'utils/images';
import { getOfferingUrl } from 'utils/navigateUtils';
import { getValidUrl } from 'utils/urlUtils';

import s from './FeaturedOffering.module.scss';

interface FeaturedOfferingProps {
  offeringInfo: IOfferingInfo;
  className?: string;
}

const FeaturedOffering = ({ offeringInfo, className }: FeaturedOfferingProps) => {
  const offeringImg = offeringInfo.media ? getValidUrl(offeringInfo.media[0]) : '';
  const fields = getPromoFields(offeringInfo, offeringInfo);

  if (!offeringImg) {
    return null;
  }

  return (
    <div className={cn(s.FeaturedOffering, className)}>
      <div className={s.FeaturedOffering__info}>
        <div className={s.FeaturedOffering__general}>
          <div>{offeringInfo.sponsor_name}</div>
          <h2 className={s.FeaturedOffering__title}>
            {offeringInfo.offering_id} {offeringInfo.offering_name}
          </h2>
          {(offeringInfo.asset_type || offeringInfo.address) && (
            <Typography sx={{ fontWeight: 'medium' }} mt={0.75} variant="caption">
              {[
                offeringInfo.asset_type,
                offeringInfo?.address?.city,
                offeringInfo?.address?.state_code
              ]
                .filter(Boolean)
                .join(', ')}
            </Typography>
          )}
          {offeringInfo.investment_highlights && (
            <Typography mt={1.5}>{offeringInfo.investment_highlights}</Typography>
          )}
        </div>
        <div className={s.FeaturedOffering__bar}>
          <ul className={s.FeaturedOffering__statsHolder}>
            {fields?.map((item) => (
              <li className={s.FeaturedOffering__statsItem} key={item?.shortTitle}>
                <strong className={s.FeaturedOffering__statsValue}>
                  {item?.range || item?.value}
                </strong>
                <div className={s.FeaturedOffering__statsCaption}>{item?.shortTitle}</div>
              </li>
            ))}
          </ul>
          <LinkButton size="large" href={getOfferingUrl(offeringInfo?.offering_id) ?? ''}>
            {'View Offering'}
          </LinkButton>
        </div>
      </div>
      <div className={s.FeaturedOffering__visual}>
        <Link href={getOfferingUrl(offeringInfo?.offering_id) ?? ''}>
          <a className={s.FeaturedOffering__imageHolder}>
            <Image
              loader={defaultLoader}
              className={s.FeaturedOffering__image}
              src={offeringImg ?? ''}
              fill
              sizes="(max-width: 768px) 100vw, 45vw"
              style={{ objectFit: 'cover' }}
              alt="Featured Offering Image"
              priority={true}
            />
            <div className={s.FeaturedOffering__ribbon}>This week’s featured offering</div>
          </a>
        </Link>
      </div>
    </div>
  );
};

export default FeaturedOffering;
